<template>
  <ValidationObserver v-slot="{ passes }">
    <b-modal
      :id="showFile ? 'modal-fullscreen-bg' : modalId"
      size="xl"
      ref="newPrescriptionModal"
      @ok="onOk"
      @show="onShow"
      @hide="onHide"
      :modal-class="showFile ? 'modal-fullscreen' : ''"
    >
      <template v-slot:modal-header="{ close }">
        <b-container>
          <div class="modal-title">
            <h3 class="text-bold text-center">
              {{ isCopying ? 'Copy' : isEdit ? 'Edit' : 'New' }} Prescription
            </h3>
            <h6 class="text-uppercase text-center">{{ customer && customer.full_name }}</h6>
            <button type="button" aria-label="Close" class="close" @click="close">
              <i class="di-remove-10" />
            </button>
          </div>
        </b-container>
      </template>

      <b-row>
        <b-col cols="6" v-if="showFile">
          <div
            :style="
              latestFile.file_name.split('.').reverse()[0] === 'pdf'
                ? 'width: 100%; height: 100%;'
                : 'width: 100%; height: 100%; position: fixed; left: -25%'
            "
          >
            <iframe
              v-if="latestFile.file_name.split('.').reverse()[0] === 'pdf'"
              :src="latestFile.full_path + '#view=FitH'"
              frameBorder="0"
              scrolling="auto"
              height="100%"
              width="100%"
            ></iframe>
            <vue-magnifier
              v-if="
                imageExtensions.includes(
                  latestFile.file_name.split('.').reverse()[0].toLowerCase(),
                )
              "
              :src="latestFile.full_path"
              :srcLarge="latestFile.full_path"
            />
          </div>
        </b-col>
        <b-col :cols="showFile ? 6 : 12">
          <b-container>
            <ul class="text-danger" v-if="errors.length">
              <li v-for="(error, index) in errors" v-bind:key="index">{{ error }}</li>
            </ul>
            <h5 class="mb-4">Prescription details</h5>
            <b-form>
              <section>
                <b-form-group label="Type" label-for="type" class="flex-fill">
                  <b-form-radio-group
                    id="type"
                    v-model="form.type_id"
                    :options="$store.state.prescriptionTypes"
                    buttons
                    button-variant="white"
                    class="custom-radio-button"
                  />
                </b-form-group>
              </section>
              <section v-if="form.type_id == 1">
                <div class="divider mt-2 mb-4"></div>
                <section>
                  <b-row>
                    <b-col cols="6">
                      <h6 class="text-uppercase mb-3">Right</h6>
                      <div class="row--2px">
                        <b-col cols="3">
                          <b-form-group label="SPH" label-for="right-sph" label-class="text-center">
                            <b-form-select
                              id="right-sph"
                              v-model="form.right_sph"
                              :options="$store.state.spheres"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="3">
                          <ValidationProvider
                            vid="right_cylinder"
                            name="Right Cylinder"
                            mode="lazy"
                            rules="required_with:@right_axis,Right Axis"
                            v-slot="validationContext"
                          >
                            <b-form-group
                              label="CYL"
                              label-for="right-cyl"
                              label-class="text-center"
                            >
                              <b-form-select
                                id="right-cyl"
                                v-model="form.right_cyl"
                                :state="getValidationState(validationContext)"
                                :options="$store.state.cylinders"
                              />
                              <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </ValidationProvider>
                        </b-col>
                        <b-col cols="3">
                          <ValidationProvider
                            vid="right_axis"
                            name="Right Axis"
                            mode="lazy"
                            rules="required_with:@right_cylinder,Right Cylinder|between:1,180"
                            v-slot="validationContext"
                          >
                            <b-form-group
                              label="AXS"
                              label-for="right-axis"
                              label-class="text-center"
                            >
                              <b-form-input
                                id="right-axis"
                                class="input-axs"
                                type="number"
                                v-disable-increment
                                min="1"
                                max="180"
                                v-model="form.right_axis"
                                :state="getValidationState(validationContext)"
                              />
                              <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </ValidationProvider>
                        </b-col>
                      </div>
                    </b-col>
                    <b-col cols="6">
                      <h6 class="text-uppercase mb-3">Left</h6>
                      <div class="row--2px">
                        <b-col cols="3">
                          <b-form-group label="SPH" label-for="left-sph" label-class="text-center">
                            <b-form-select
                              id="left-sph"
                              v-model="form.left_sph"
                              :options="$store.state.spheres"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="3">
                          <ValidationProvider
                            vid="left_cylinder"
                            name="Left Cylinder"
                            mode="lazy"
                            rules="required_with:@left_axis,Left Axis"
                            v-slot="validationContext"
                          >
                            <b-form-group
                              label="CYL"
                              label-for="left-cyl"
                              label-class="text-center"
                            >
                              <b-form-select
                                id="left-cyl"
                                v-model="form.left_cyl"
                                :state="getValidationState(validationContext)"
                                :options="$store.state.cylinders"
                              />
                              <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </ValidationProvider>
                        </b-col>
                        <b-col cols="3">
                          <ValidationProvider
                            vid="left_axis"
                            name="Left Axis"
                            mode="lazy"
                            rules="required_with:@left_cylinder,Left Cylinder|between:1,180"
                            v-slot="validationContext"
                          >
                            <b-form-group
                              label="AXS"
                              label-for="left-axis"
                              label-class="text-center"
                            >
                              <b-form-input
                                id="left-axis"
                                class="input-axs"
                                type="number"
                                v-disable-increment
                                min="1"
                                max="180"
                                v-model="form.left_axis"
                                :state="getValidationState(validationContext)"
                              />
                              <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </ValidationProvider>
                        </b-col>
                      </div>
                    </b-col>
                  </b-row>
                </section>
                <div class="divider mt-3 mb-4"></div>
                <section>
                  <h6 class="text-uppercase mb-3">Add</h6>
                  <b-form-radio-group
                    id="is-monocular"
                    v-model="isMonocular"
                    :options="addTypes"
                    buttons
                    button-variant="white"
                    class="custom-radio-button mb-3"
                  />
                  <b-row v-show="!isMonocular">
                    <b-col cols="2">
                      <b-form-group label="Near add" label-for="near-add">
                        <b-form-input
                          id="near-add"
                          name="near_add"
                          type="number"
                          v-disable-increment
                          v-model="near_add"
                          @change="updateBothNearAdd"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="2">
                      <b-form-group label="Int add" label-for="int-add">
                        <b-form-input
                          id="int-add"
                          name="int_add"
                          type="number"
                          v-disable-increment
                          v-model="int_add"
                          @change="updateBothIntAdd"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <div v-show="isMonocular">
                    <b-row>
                      <b-col cols="4">
                        <h6 class="text-uppercase mb-3">Right Add</h6>
                        <b-row>
                          <b-col cols="6">
                            <b-form-group label="Near add" label-for="right-near-add">
                              <b-form-input
                                id="right-near-add"
                                name="right_near_add"
                                type="number"
                                v-disable-increment
                                v-model="form.spectacles.right_near_add"
                                @input="
                                  form.spectacles.left_near_add = form.spectacles.right_near_add
                                "
                              />
                            </b-form-group>
                          </b-col>
                          <b-col cols="6">
                            <b-form-group label="Int add" label-for="right-int-add">
                              <b-form-input
                                id="right-int-add"
                                name="right_int_add"
                                type="number"
                                v-disable-increment
                                v-model="form.spectacles.right_int_add"
                                @input="
                                  form.spectacles.left_int_add = form.spectacles.right_int_add
                                "
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </b-col>
                      <b-col cols="4">
                        <h6 class="text-uppercase mb-3">Left Add</h6>
                        <b-row>
                          <b-col cols="6">
                            <b-form-group label="Near add" label-for="left-near-add">
                              <b-form-input
                                id="left-near-add"
                                name="left_near_add"
                                type="number"
                                v-disable-increment
                                v-model="form.spectacles.left_near_add"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col cols="6">
                            <b-form-group label="Int add" label-for="left-int-add">
                              <b-form-input
                                id="left-int-add"
                                name="left_int_add"
                                type="number"
                                v-disable-increment
                                v-model="form.spectacles.left_int_add"
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </div>
                </section>
                <div class="divider mt-1 mb-4"></div>
                <section>
                  <b-button variant="icon" v-b-toggle.prism>
                    <i class="di-arrow-down-12 when-closed" />
                    <i class="di-arrow-up-12 when-opened" />
                  </b-button>
                  <h5 class="d-inline-block mb-4 ml-2">Prism</h5>
                  <b-collapse id="prism" v-model="prismSectionVisible">
                    <b-row>
                      <b-col cols="6">
                        <h6 class="text-uppercase mb-3">Horizontal Right</h6>
                      </b-col>
                      <b-col cols="6">
                        <h6 class="text-uppercase mb-3">Horizontal Left</h6>
                      </b-col>
                      <b-col cols="6">
                        <PrismInput
                          label="Near"
                          check-text="Out"
                          uncheck-text="In"
                          v-model="form.spectacles.right_horiz_near_prism"
                        />
                        <div v-if="isCanadaRegion && form.spectacles.right_horiz_near_prism" class="text-warning mt-2 mb-3">
                          <small>PlasticPlus doesn't support near prism values, please edit or order manually</small>
                        </div>
                      </b-col>

                      <b-col cols="6">
                        <PrismInput
                          label="Near"
                          check-text="Out"
                          uncheck-text="In"
                          v-model="form.spectacles.left_horiz_near_prism"
                        />
                        <div v-if="isCanadaRegion && form.spectacles.left_horiz_near_prism" class="text-warning mt-2 mb-3">
                          <small>PlasticPlus doesn't support near prism values, please edit or order manually</small>
                        </div>
                      </b-col>

                      <b-col cols="6">
                        <PrismInput
                          label="Distance"
                          check-text="Out"
                          uncheck-text="In"
                          v-model="form.spectacles.right_horiz_dist_prism"
                        />
                      </b-col>

                      <b-col cols="6">
                        <PrismInput
                          label="Distance"
                          check-text="Out"
                          uncheck-text="In"
                          v-model="form.spectacles.left_horiz_dist_prism"
                        />
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col cols="6">
                        <h6 class="text-uppercase mb-3">Vertical Right</h6>
                      </b-col>
                      <b-col cols="6">
                        <h6 class="text-uppercase mb-3">Vertical Left</h6>
                      </b-col>

                      <b-col cols="6">
                        <PrismInput
                          label="Near"
                          check-text="Up"
                          uncheck-text="Down"
                          v-model="form.spectacles.right_vert_near_prism"
                        />
                        <div v-if="isCanadaRegion && form.spectacles.right_vert_near_prism" class="text-warning mt-2 mb-3">
                          <small>PlasticPlus doesn't support near prism values, please edit or order manually</small>
                        </div>
                      </b-col>
                      <b-col cols="6">
                        <PrismInput
                          label="Near"
                          check-text="Up"
                          uncheck-text="Down"
                          v-model="form.spectacles.left_vert_near_prism"
                        />
                        <div v-if="isCanadaRegion && form.spectacles.left_vert_near_prism" class="text-warning mt-2 mb-3">
                          <small>PlasticPlus doesn't support near prism values, please edit or order manually</small>
                        </div>
                      </b-col>

                      <b-col cols="6">
                        <PrismInput
                          label="Distance"
                          check-text="Up"
                          uncheck-text="Down"
                          v-model="form.spectacles.right_vert_dist_prism"
                        />
                      </b-col>

                      <b-col cols="6">
                        <PrismInput
                          label="Distance"
                          check-text="Up"
                          uncheck-text="Down"
                          v-model="form.spectacles.left_vert_dist_prism"
                        />
                      </b-col>
                    </b-row>
                  </b-collapse>
                </section>
                <div class="divider mb-4"></div>
                <section>
                  <!-- IOGO-336 -->
                  <!-- <div class="box box--info box--icon mb-4">
              <div class="box-icon">
                <i class="di-information-10" />
              </div>
              <div class="box-header">
                <div class="box-header__title">
                  <p class="text-semi-bold mb-2">Source options</p>
                  <p>Quisque quis velit ex. Integer lacinia vehicula neque at dapibus.</p>
                </div>
              </div>
            </div> -->
                  <ValidationProvider
                    vid="source"
                    name="Source"
                    mode="lazy"
                    rules="required"
                    v-slot="validationContext"
                  >
                    <b-form-group label="Source" label-for="source">
                      <b-form-radio-group
                        id="source"
                        v-model="form.source_id"
                        name="source_id"
                        buttons
                        class="custom-radio-tag"
                        :state="getValidationState(validationContext)"
                      >
                        <b-form-radio
                          v-for="source in $store.state.prescriptionSources"
                          v-bind:key="source.value"
                          :value="source.value"
                          button
                          :button-variant="source.color"
                        >
                          <span>{{ source.text }}</span>
                        </b-form-radio>
                      </b-form-radio-group>
                      <b-form-invalid-feedback class="d-block">
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </ValidationProvider>
                  <b-row>
                    <b-col cols="6">
                      <b-form-group label="Outlet" label-for="outlet">
                        <b-form-select
                          id="outlet"
                          :options="$store.getters.outletOptions"
                          v-model="form.outlet_id"
                        >
                        </b-form-select>
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group label="Expiry date" label-for="expiry">
                        <b-form-input
                          id="expiry"
                          name="expiry_date_stamp"
                          type="date"
                          v-model="form.expiry_date_stamp"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-form-group label="Internal Note" label-for="internal-note">
                    <b-form-textarea
                      id="internal-note"
                      v-model="form.internal_note"
                      rows="3"
                      placeholder="Type your internal notes here..."
                    />
                  </b-form-group>
                  <b-form-group label="Customer Notes" label-for="note">
                    <b-form-textarea
                      id="note"
                      v-model="form.note"
                      rows="3"
                      placeholder="Type your notes here..."
                    />
                  </b-form-group>
                  <b-form-group label="Printed Notes" label-for="printed-note" class="mb-4">
                    <b-form-textarea
                      id="printed-note"
                      v-model="form.printed_note"
                      rows="3"
                      placeholder="Type your notes here..."
                    />
                  </b-form-group>
                  <b-form-group
                    v-if="form.files && form.files.length"
                    label="File"
                    label-for="prescription-file"
                    class="mb-4"
                  >
                    <FileCard
                      id="prescription-file"
                      :files="form.files"
                      removable
                      @removeFile="removePrescriptionFile"
                    />
                  </b-form-group>
                </section>
                <div class="divider mb-4"></div>
                <section class="pb-4">
                  <ValidationProvider
                    name="File"
                    rules="ext:jpeg,jpg,png,bmp,pdf,doc,docx|fileSize:10024"
                    ref="fileValidator"
                  >
                    <h5>Upload a prescription copy</h5>
                    <b-form-file
                      ref="fileInput"
                      :value="file"
                      class="d-none"
                      plain
                      @change="handleFileInputChange"
                    />
                    <div v-if="file">
                      <b-button class="my-3 px-5" variant="success"> ✔ </b-button>
                      <span class="ml-3">Upload successfull!</span>
                      <b-button variant="link" @click="file = null">
                        <u>Re-upload</u>
                      </b-button>
                    </div>
                    <div v-else>
                      <b-button
                        class="my-3"
                        variant="outline-secondary"
                        @click="$refs.fileInput.$el.click()"
                      >
                        Upload a file
                      </b-button>
                      <b-button class="my-3 ml-2" variant="outline-secondary">
                        Take a photo
                      </b-button>
                    </div>
                    <FilePreview :file="file" :error="fileError" />
                  </ValidationProvider>
                </section>
              </section>

              <section v-if="form.type_id == 2">
                <div class="divider mt-2 mb-4"></div>
                <section>
                  <b-row>
                    <b-col cols="6">
                      <h6 class="text-uppercase mb-3">Right</h6>
                      <div class="row--2px">
                        <b-col cols="3">
                          <b-form-group label="SPH" label-for="right-sph" label-class="text-center">
                            <b-form-select
                              id="right-sph"
                              v-model="form.right_sph"
                              :options="$store.state.spheres"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="3">
                          <ValidationProvider
                            vid="right_cylinder"
                            name="Right Cylinder"
                            mode="lazy"
                            rules="required_with:@right_axis,Right Axis"
                            v-slot="validationContext"
                          >
                            <b-form-group
                              label="CYL"
                              label-for="right-cyl"
                              label-class="text-center"
                            >
                              <b-form-select
                                id="right-cyl"
                                v-model="form.right_cyl"
                                :state="getValidationState(validationContext)"
                                :options="$store.state.cylinders"
                              />
                              <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </ValidationProvider>
                        </b-col>
                        <b-col cols="3">
                          <ValidationProvider
                            vid="right_axis"
                            name="Right Axis"
                            mode="lazy"
                            rules="required_with:@right_cylinder,Right Cylinder|between:1,180"
                            v-slot="validationContext"
                          >
                            <b-form-group
                              label="AXS"
                              label-for="right-axis"
                              label-class="text-center"
                            >
                              <b-form-input
                                id="right-axis"
                                class="input-axs"
                                type="number"
                                v-disable-increment
                                min="1"
                                max="180"
                                v-model="form.right_axis"
                                :state="getValidationState(validationContext)"
                              />
                              <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </ValidationProvider>
                        </b-col>
                      </div>
                    </b-col>
                    <b-col cols="6">
                      <h6 class="text-uppercase mb-3">Left</h6>
                      <div class="row--2px">
                        <b-col cols="3">
                          <b-form-group label="SPH" label-for="left-sph" label-class="text-center">
                            <b-form-select
                              id="left-sph"
                              v-model="form.left_sph"
                              :options="$store.state.spheres"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="3">
                          <ValidationProvider
                            vid="left_cylinder"
                            name="Left Cylinder"
                            mode="lazy"
                            rules="required_with:@left_axis,Left Axis"
                            v-slot="validationContext"
                          >
                            <b-form-group
                              label="CYL"
                              label-for="left-cyl"
                              label-class="text-center"
                            >
                              <b-form-select
                                id="left-cyl"
                                v-model="form.left_cyl"
                                :state="getValidationState(validationContext)"
                                :options="$store.state.cylinders"
                              />
                              <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </ValidationProvider>
                        </b-col>
                        <b-col cols="3">
                          <ValidationProvider
                            vid="left_axis"
                            name="Left Axis"
                            mode="lazy"
                            rules="required_with:@left_cylinder,Left Cylinder|between:1,180"
                            v-slot="validationContext"
                          >
                            <b-form-group
                              label="AXS"
                              label-for="left-axis"
                              label-class="text-center"
                            >
                              <b-form-input
                                id="left-axis"
                                class="input-axs"
                                type="number"
                                v-disable-increment
                                min="1"
                                max="180"
                                v-model="form.left_axis"
                                :state="getValidationState(validationContext)"
                              />
                              <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </ValidationProvider>
                        </b-col>
                      </div>
                    </b-col>
                  </b-row>
                </section>

                <section>
                  <b-row>
                    <b-col cols="6">
                      <div class="row--2px">
                        <b-row>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="right-contact_lens-add_near"
                              name="Right Add Near"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group
                                label="Add Near"
                                label-for="right-contact_lens-add_near"
                              >
                                <b-form-input
                                  id="right-contact_lens-add_near"
                                  name="contact_lens_right_supplier"
                                  type="text"
                                  v-model="form.contact_lens.right_near_add"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>

                          <b-col cols="12">
                            <ValidationProvider
                              vid="right-contact_lens-add_near"
                              name="Right Add Near"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group
                                label="Add Near Type"
                                label-for="right-contact_lens-add_near"
                              >
                                <b-form-select
                                  v-model="form.contact_lens.right_near_add_enum"
                                  :options="enum_types"
                                  :state="getValidationState(validationContext)"
                                  id="right-contact_lens-add_near_type"
                                  name="contact_lens_right_supplier_type"
                                ></b-form-select>

                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                                <b-form-invalid-feedback
                                  :force-show="showAddErrors && !!contactLensAddErrors.right"
                                >
                                  {{ contactLensAddErrors.right }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>

                          <b-col cols="12">
                            <ValidationProvider
                              vid="right-contact_lens-supplier"
                              name="Right Supplier"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group
                                label="Supplier"
                                label-for="right-contact_lens-supplier"
                              >
                                <b-form-input
                                  id="right-contact_lens-supplier"
                                  name="contact_lens_right_supplier"
                                  type="text"
                                  v-model="form.contact_lens.right_supplier"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="right-contact_lens-type"
                              name="Right Type"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Type" label-for="right-contact_lens-type">
                                <b-form-input
                                  id="right-contact_lens-type"
                                  name="contact_lens_right_type"
                                  type="text"
                                  v-model="form.contact_lens.right_type"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="right-contact_lens-brand"
                              name="Right Brand"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Brand" label-for="right-contact_lens-brand">
                                <b-form-input
                                  id="right-contact_lens-brand"
                                  name="contact_lens_right_brand"
                                  type="text"
                                  v-model="form.contact_lens.right_brand"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="right-contact_lens-base-curve"
                              name="Right Base Curve"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group
                                label="Base Curve"
                                label-for="right-contact_lens-base-curve"
                              >
                                <b-form-input
                                  id="right-contact_lens-base-curve"
                                  name="contact_lens_right_base_curve"
                                  type="number"
                                  v-disable-increment
                                  v-model="form.contact_lens.right_base_curve"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="right-contact_lens-diameter"
                              name="Right Diameter"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group
                                label="Diameter"
                                label-for="right-contact_lens-diameter"
                              >
                                <b-form-input
                                  id="right-contact_lens-diameter"
                                  name="contact_lens_right_diameter"
                                  type="text"
                                  v-disable-increment
                                  v-model="form.contact_lens.right_diameter"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="right-contact_lens-color"
                              name="Right Color"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Color" label-for="right-contact_lens-color">
                                <b-form-input
                                  id="right-contact_lens-color"
                                  name="contact_lens_right_color"
                                  type="text"
                                  v-model="form.contact_lens.right_colour"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="right-contact_lens-notes"
                              name="Right Notes"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Notes" label-for="right-contact_lens-notes">
                                <b-form-input
                                  id="right-contact_lens-notes"
                                  name="contact_lens_right_notes"
                                  type="text"
                                  v-model="form.contact_lens.right_notes"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                        </b-row>
                      </div>
                    </b-col>
                    <b-col cols="6">
                      <div class="row--2px">
                        <b-row>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="left-contact_lens-add_near"
                              name="Left Add Near"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Add Near" label-for="left-contact_lens-add_near">
                                <b-form-input
                                  id="left-contact_lens-add_near"
                                  name="contact_lens_left_supplier"
                                  type="text"
                                  v-model="form.contact_lens.left_near_add"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>

                          <b-col cols="12">
                            <ValidationProvider
                              vid="left-contact_lens-add_near_enum"
                              name="Left Add Near"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group
                                label="Add Near Type"
                                label-for="left-contact_lens-add_near_enum"
                              >
                                <b-form-select
                                  v-model="form.contact_lens.left_near_add_enum"
                                  :options="enum_types"
                                  :state="getValidationState(validationContext)"
                                  id="left-contact_lens-add_near_enum"
                                  name="contact_lens_left_supplier_type_enum"
                                ></b-form-select>

                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                                <b-form-invalid-feedback
                                  :force-show="showAddErrors && !!contactLensAddErrors.left"
                                >
                                  {{ contactLensAddErrors.left }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>

                          <b-col cols="12">
                            <ValidationProvider
                              vid="left-contact_lens-supplier"
                              name="left Supplier"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Supplier" label-for="left-contact_lens-supplier">
                                <b-form-input
                                  id="left-contact_lens-supplier"
                                  name="contact_lens_left_supplier"
                                  type="text"
                                  v-model="form.contact_lens.left_supplier"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="left-contact_lens-type"
                              name="left Type"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Type" label-for="left-contact_lens-type">
                                <b-form-input
                                  id="left-contact_lens-type"
                                  name="contact_lens_left_type"
                                  type="text"
                                  v-model="form.contact_lens.left_type"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="left-contact_lens-brand"
                              name="left Brand"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Brand" label-for="left-contact_lens-brand">
                                <b-form-input
                                  id="left-contact_lens-brand"
                                  name="contact_lens_left_brand"
                                  type="text"
                                  v-model="form.contact_lens.left_brand"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="left-contact_lens-base-curve"
                              name="left Base Curve"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group
                                label="Base Curve"
                                label-for="left-contact_lens-base-curve"
                              >
                                <b-form-input
                                  id="left-contact_lens-base-curve"
                                  name="contact_lens_left_base_curve"
                                  type="number"
                                  v-disable-increment
                                  v-model="form.contact_lens.left_base_curve"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="left-contact_lens-diameter"
                              name="left Diameter"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Diameter" label-for="left-contact_lens-diameter">
                                <b-form-input
                                  id="left-contact_lens-diameter"
                                  name="contact_lens_left_diameter"
                                  type="text"
                                  v-disable-increment
                                  v-model="form.contact_lens.left_diameter"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="left-contact_lens-colour"
                              name="left Colour"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Color" label-for="left-contact_lens-colour">
                                <b-form-input
                                  id="left-contact_lens-colour"
                                  name="contact_lens_left_colour"
                                  type="text"
                                  v-model="form.contact_lens.left_colour"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                          <b-col cols="12">
                            <ValidationProvider
                              vid="left-contact_lens-notes"
                              name="left Notes"
                              mode="lazy"
                              v-slot="validationContext"
                            >
                              <b-form-group label="Notes" label-for="left-contact_lens-notes">
                                <b-form-input
                                  id="left-contact_lens-notes"
                                  name="contact_lens_left_notes"
                                  type="text"
                                  v-model="form.contact_lens.left_notes"
                                  :state="getValidationState(validationContext)"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </ValidationProvider>
                          </b-col>
                        </b-row>
                      </div>
                    </b-col>
                  </b-row>
                </section>

                <section>
                  <!-- IOGO-336 -->
                  <!-- <div class="box box--info box--icon mb-4">
              <div class="box-icon">
                <i class="di-information-10" />
              </div>
              <div class="box-header">
                <div class="box-header__title">
                  <p class="text-semi-bold mb-2">Source options</p>
                  <p>Quisque quis velit ex. Integer lacinia vehicula neque at dapibus.</p>
                </div>
              </div>
            </div> -->
                  <ValidationProvider
                    vid="source"
                    name="Source"
                    mode="lazy"
                    rules="required"
                    v-slot="validationContext"
                  >
                    <b-form-group label="Source" label-for="source">
                      <b-form-radio-group
                        id="source"
                        v-model="form.source_id"
                        name="source_id"
                        buttons
                        class="custom-radio-tag"
                        :state="getValidationState(validationContext)"
                      >
                        <b-form-radio
                          v-for="source in $store.state.prescriptionSources"
                          v-bind:key="source.value"
                          :value="source.value"
                          button
                          :button-variant="source.color"
                        >
                          <span>{{ source.text }}</span>
                        </b-form-radio>
                      </b-form-radio-group>
                      <b-form-invalid-feedback class="d-block">
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </ValidationProvider>
                  <b-row>
                    <b-col cols="6">
                      <b-form-group label="Outlet" label-for="outlet">
                        <b-form-select
                          id="outlet"
                          :options="$store.getters.outletOptions"
                          v-model="form.outlet_id"
                        >
                        </b-form-select>
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group label="Expiry date" label-for="expiry">
                        <b-form-input
                          id="expiry"
                          name="expiry_date_stamp"
                          type="date"
                          v-model="form.expiry_date_stamp"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-form-group label="Internal Note" label-for="internal-note">
                    <b-form-textarea
                      id="internal-note"
                      v-model="form.internal_note"
                      rows="3"
                      placeholder="Type your internal notes here..."
                    />
                  </b-form-group>
                  <b-form-group label="Customer Notes" label-for="note">
                    <b-form-textarea
                      id="note"
                      v-model="form.note"
                      rows="3"
                      placeholder="Type your notes here..."
                    />
                  </b-form-group>
                  <b-form-group label="Printed Notes" label-for="printed-note" class="mb-4">
                    <b-form-textarea
                      id="printed-note"
                      v-model="form.printed_note"
                      rows="3"
                      placeholder="Type your notes here..."
                    />
                  </b-form-group>
                  <b-form-group
                    v-if="form.files && form.files.length"
                    label="File"
                    label-for="prescription-file"
                    class="mb-4"
                  >
                    <FileCard
                      id="prescription-file"
                      :files="form.files"
                      removable
                      @removeFile="removePrescriptionFile"
                    />
                  </b-form-group>
                </section>
              </section>
            </b-form>
          </b-container>
        </b-col>
      </b-row>

      <template v-slot:modal-footer="{ cancel }">
        <div class="flex-fill m-0">
          <b-container>
            <b-alert
              v-if="showNearPrismConfirmation"
              show
              variant="warning"
              class="mb-3"
            >
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <strong>Near Prism Warning</strong>
                  <p class="mb-0 mt-1">PlasticPlus doesn't support near prism values, please edit or order manually</p>
                </div>
                <div class="ml-3 d-flex">
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="showNearPrismConfirmation = false"
                    class="mr-2"
                  >
                    Cancel
                  </b-button>
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="confirmAndSubmit"
                  >
                    {{ isEdit ? 'Save' : 'Create' }}
                  </b-button>
                </div>
              </div>
            </b-alert>
            <div class="d-flex justify-content-end">
              <b-button variant="outline-secondary" @click="cancel()">Cancel</b-button>
              <b-button
                variant="primary"
                @click="passes(submit)"
                class="ml-3"
                :disabled="isLoading"
              >
                <b-spinner v-if="isLoading" small />
                <span v-else> {{ isEdit ? 'Save' : 'Create' }} Prescription </span>
              </b-button>
            </div>
          </b-container>
        </div>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapState } from 'vuex';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import VueMagnifier from '@/components/vue-magnifier.vue';
import apiPrescriptions from '../api/prescriptions';
import apiFiles from '../api/files';
import findDuplicatePrescription from '../support/duplicatePrescription';
import PrismInput from './PrismInput.vue';
import FilePreview from './FilePreview.vue';
import FileCard from './FileCard.vue';

export default {
  name: 'NewPrescriptionModal',
  components: {
    PrismInput,
    ValidationProvider,
    ValidationObserver,
    FilePreview,
    FileCard,
    VueMagnifier,
  },
  data() {
    return {
      enum_types: [
        {
          value: null,
          text: 'None',
        },
        {
          value: 'LOW',
          text: 'LOW',
        },
        {
          value: 'MEDIUM',
          text: 'MEDIUM',
        },
        {
          value: 'HIGH',
          text: 'HIGH',
        },
      ],
      addTypes: [
        {
          value: false,
          text: 'Binocular',
        },
        {
          value: true,
          text: 'Monocular',
        },
      ],
      isMonocular: false,
      isLoading: false,
      form: this.initForm(),
      file: null,
      fileError: '',
      near_add: null,
      int_add: null,
      errors: [],
      imageExtensions: ['png', 'jpg', 'jpeg'],
      newUploadImage: false,
      showNearPrismConfirmation: false,
      nearPrismConfirmed: false,
      prismSectionVisible: false,
      showAddErrors: false,
    };
  },
  props: {
    customer: Object,
    prescription: Object,
    isOnPageEdit: Boolean,
    isCopy: Boolean,
    defaultTypeId: Number,
    modalId: {
      type: String,
      default: 'new-prescription-modal',
    },
  },
  computed: {
    ...mapState('auth', {
      selectedOutletId: state => state.selectedOutletId,
    }),
    currentRegion() {
      return this.$store.getters['auth/currentRegion'];
    },
    isCanadaRegion() {
      return this.currentRegion && this.currentRegion.country_code === 'CA';
    },
    hasNearPrismValues() {
      const spectacles = this.form.spectacles;
      return !!(
        spectacles.right_horiz_near_prism ||
        spectacles.left_horiz_near_prism ||
        spectacles.right_vert_near_prism ||
        spectacles.left_vert_near_prism
      );
    },
    contactLensAddErrors() {
      const errors = {};
      if (this.form.type_id != 2) return errors;
      ['right', 'left'].forEach(eye => {
        const numeric = String(this.form.contact_lens[`${eye}_near_add`] ?? '').trim();
        if (numeric && this.form.contact_lens[`${eye}_near_add_enum`]) {
          errors[eye] = `Enter either a numeric Add Near or select an Add Near Type for the ${eye} eye. Clear the other field to continue.`;
        }
      });
      return errors;
    },
    isCopying() {
      return this.isCopy && !!this.prescription && !!this.prescription.id;
    },
    isEdit() {
      return !this.isCopying && !!this.prescription && !!this.prescription.id;
    },
    prescriptionForm() {
      if (this.isEdit || this.isCopying) {
        const spectacles = this.prescription.spectacles || {};
        const contact_lens = this.prescription.contact_lens || null;
        this.isMonocular = this.prescription.add_type === 'monocular';
        console.log(this.isMonocular);
        return {
          add_type: this.prescription.add_type,
          right_sph: this.prescription.right_sph,
          right_cyl: Number(this.prescription.right_cyl),
          right_axis: this.prescription.right_axis > 0 ? this.prescription.right_axis : null,
          left_sph: this.prescription.left_sph,
          left_cyl: Number(this.prescription.left_cyl),
          left_axis: this.prescription.left_axis > 0 ? this.prescription.left_axis : null,
          spectacles: {
            add_near: spectacles.add_near || null,
            add_int: spectacles.add_int || null,
            right_near_add: spectacles.right_near_add || null,
            right_int_add: spectacles.right_int_add || null,
            left_near_add: spectacles.left_near_add || null,
            left_int_add: spectacles.left_int_add || null,
            right_horiz_dist_prism: spectacles.right_horiz_dist_prism || null,
            right_horiz_near_prism: spectacles.right_horiz_near_prism || null,
            right_vert_dist_prism: spectacles.right_vert_dist_prism || null,
            right_vert_near_prism: spectacles.right_vert_near_prism || null,
            left_horiz_dist_prism: spectacles.left_horiz_dist_prism || null,
            left_horiz_near_prism: spectacles.left_horiz_near_prism || null,
            left_vert_dist_prism: spectacles.left_vert_dist_prism || null,
            left_vert_near_prism: spectacles.left_vert_near_prism || null,
          },
          contact_lens: contact_lens
            ? {
                left_base_curve: contact_lens.left_base_curve,
                left_brand: contact_lens.left_brand,
                left_colour: contact_lens.left_colour,
                left_diameter: contact_lens.left_diameter,
                left_lens: contact_lens.left_lens,
                left_near_add: contact_lens.left_near_add,
                left_notes: contact_lens.left_notes,
                left_supplier: contact_lens.left_supplier,
                left_type: contact_lens.left_type,
                right_base_curve: contact_lens.right_base_curve,
                right_brand: contact_lens.right_brand,
                right_colour: contact_lens.right_colour,
                right_diameter: contact_lens.right_diameter,
                right_lens: contact_lens.right_lens,
                right_near_add: contact_lens.right_near_add,
                right_notes: contact_lens.right_notes,
                right_supplier: contact_lens.right_supplier,
                right_type: contact_lens.right_type,
                left_near_add_enum: contact_lens.left_near_add_enum,
                right_near_add_enum: contact_lens.right_near_add_enum,
              }
            : this.initForm().contact_lens,
          type_id: this.prescription.type_id,
          source_id: this.prescription.source_id,
          outlet_id:
            this.isCopying && !this.prescription.outlet_id
              ? this.selectedOutletId
              : this.prescription.outlet_id,
          internal_note: this.prescription.internal_note,
          note: this.prescription.note,
          printed_note: this.prescription.printed_note,
          // the create endpoint re-links sent file ids, which would take them off the original
          files: this.isCopying ? [] : this.prescription.files,
          expiry_date_stamp: this.prescription.expiry_date_stamp,
        };
      }
      return this.initForm();
    },
    latestFile() {
      if (!this.form.files || !this.form.files.length) return null;
      // Return the last file in the array (most recently added)
      return this.form.files[this.form.files.length - 1];
    },
    showFile() {
      return this.latestFile && this.latestFile.full_path;
    },
  },
  methods: {
    initForm() {
      return {
        add_type: 'binocular',
        right_sph: 0,
        right_cyl: 0,
        right_axis: null,
        left_sph: 0,
        left_cyl: 0,
        left_axis: null,
        spectacles: {
          add_near: null,
          add_int: null,
          right_near_add: null,
          right_int_add: null,
          left_near_add: null,
          left_int_add: null,
          right_horiz_dist_prism: null,
          right_horiz_near_prism: null,
          right_vert_dist_prism: null,
          right_vert_near_prism: null,
          left_horiz_dist_prism: null,
          left_horiz_near_prism: null,
          left_vert_dist_prism: null,
          left_vert_near_prism: null,
        },
        type_id: this._.get(this.$store.state.prescriptionTypes, '0.value', null),
        source_id: null,
        outlet_id: this.selectedOutletId,
        internal_note: null,
        note: null,
        printed_note: null,
        expiry_date_stamp: null,
        contact_lens: {
          left_base_curve: null,
          left_brand: null,
          left_colour: null,
          left_diameter: null,
          left_lens: null,
          left_near_add: null,
          left_notes: null,
          left_supplier: null,
          left_type: null,
          right_base_curve: null,
          right_brand: null,
          right_colour: null,
          right_diameter: null,
          right_lens: null,
          right_near_add: null,
          right_notes: null,
          right_supplier: null,
          right_type: null,
          left_near_add_enum: null,
          right_near_add_enum: null,
        },
      };
    },
    initData() {
      return {
        addTypes: [
          {
            value: false,
            text: 'Binocular',
          },
          {
            value: true,
            text: 'Monocular',
          },
        ],
        isMonocular: false,
        isLoading: false,
        form: this.initForm(),
        near_add: null,
        int_add: null,
        errors: [],
        file: null,
        fileError: '',
        showNearPrismConfirmation: false,
        nearPrismConfirmed: false,
        prismSectionVisible: false,
        showAddErrors: false,
      };
    },
    onHide() {
      Object.assign(this.$data, this.initData());
    },
    onShow() {
      this.$nextTick(() => {
        if (this.isEdit || this.isCopying) {
          const spectacles = this.prescription.spectacles || {};
          // For binocular mode, check add_near first, then fall back to matching left/right values
          if (this.prescription.add_type === 'binocular') {
            this.near_add = spectacles.add_near || spectacles.left_near_add;
            this.int_add = spectacles.add_int || spectacles.left_int_add;
          } else {
            // For monocular mode, check if left and right match (legacy behavior)
            if (spectacles.left_near_add === spectacles.right_near_add) {
              this.near_add = spectacles.left_near_add;
            }
            if (spectacles.left_int_add === spectacles.right_int_add) {
              this.int_add = spectacles.left_int_add;
            }
          }
          this.form = this.prescriptionForm;

          // Auto-expand prism section if prescription has any prism values
          const hasPrismValues = !!(
            spectacles.right_horiz_near_prism ||
            spectacles.left_horiz_near_prism ||
            spectacles.right_horiz_dist_prism ||
            spectacles.left_horiz_dist_prism ||
            spectacles.right_vert_near_prism ||
            spectacles.left_vert_near_prism ||
            spectacles.right_vert_dist_prism ||
            spectacles.left_vert_dist_prism
          );
          if (hasPrismValues) {
            this.prismSectionVisible = true;
          }
        } else {
          this.form.outlet_id = this.selectedOutletId;
          if (this.defaultTypeId) {
            this.form.type_id = this.defaultTypeId;
          }
        }
      });
    },
    onOk(e) {
      this.$emit('ok', e);
    },
    updateBothNearAdd() {
      // In binocular mode, set the single add_near field
      this.form.spectacles.add_near = this.near_add;
      // Also set individual fields for backward compatibility
      this.form.spectacles.right_near_add = this.near_add;
      this.form.spectacles.left_near_add = this.near_add;
    },
    updateBothIntAdd() {
      // In binocular mode, set the single add_int field
      this.form.spectacles.add_int = this.int_add;
      // Also set individual fields for backward compatibility
      this.form.spectacles.right_int_add = this.int_add;
      this.form.spectacles.left_int_add = this.int_add;
    },
    confirmAndSubmit() {
      this.nearPrismConfirmed = true;
      this.showNearPrismConfirmation = false;
      this.submit();
    },
    // True when the prescription can be saved: no identical live one exists, or staff said so (DP-4856).
    async confirmDuplicate() {
      const response = await apiPrescriptions.getCustomerPrescription(this.customer.id);
      const existing = findDuplicatePrescription(this.form, response.data.data);
      if (!existing) return true;

      const rx = existing.standardised_prescription_display_array || {};
      const source = this._.get(existing, 'source.data.name');
      return this.$bvModal.msgBoxConfirm(
        `This customer already has an identical spectacle prescription: ${rx.right} / ${rx.left}, ` +
          `created on ${this.$moment(existing.created_at).format('D MMM YYYY h:mm a')}` +
          `${source ? ` (${source})` : ''}. Save it again anyway?`,
        {
          title: 'Identical prescription exists',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Save anyway',
          cancelVariant: 'outline-secondary',
          cancelTitle: 'Cancel',
          hideHeaderClose: false,
          centered: true,
          headerClass: 'border-bottom p-3',
          bodyClass: 'p-4',
          footerClass: 'bg-white p-2 justify-content-center',
        },
      );
    },
    submit: async function submit() {
      if (this.isLoading) {
        console.warn('Submit already in progress');
        return;
      }

      this.showAddErrors = true;
      if (Object.keys(this.contactLensAddErrors).length) {
        return;
      }

      if (this.isCanadaRegion && this.hasNearPrismValues && !this.nearPrismConfirmed) {
        this.showNearPrismConfirmation = true;
        return;
      }

      this.isLoading = true;

      if (!this.isEdit && !this.isCopying && this.customer && this.form.type_id == 1) {
        const confirmed = await this.confirmDuplicate();
        if (!confirmed) {
          this.isLoading = false;
          return;
        }
      }

      // Transform files array to contain only IDs for API submission
      const formData = { ...this.form };
      if (formData.files && formData.files.length > 0) {
        formData.files = formData.files.map(file => file.id);
      }
      
      if (this.isEdit) {
        apiPrescriptions
          .putPrescription(this.prescription.id, formData)
          .then(response => {
            // Check for validation errors in response (same pattern as create flow)
            const error = this._.get(response, 'data.error');
            const data = this._.get(response, 'data.data');
            if (error) {
              let errorText = error;
              if (data && Array.isArray(data) && data.length > 0) {
                data.forEach(err => {
                  errorText += ` ${err}`;
                });
              }
              this.$store.dispatch('showErrorAlert', errorText);
              this.isLoading = false;
              return;
            }
            this.$nextTick(() => {
              this.$refs.newPrescriptionModal.hide('ok');
              this.$store.dispatch(
                'showSuccessAlert',
                'Prescription has been successfully edited!',
              );
            });
            if (this.isOnPageEdit) {
              // Update prescription object with the latest form data including files
              const updatedPrescription = { ...this.prescription };
              if (this.form.files) {
                updatedPrescription.files = this.form.files;
              }
              this.$emit('prescriptionDataSent', {
                prescription: updatedPrescription,
              });

              if (this.customer) {
                this.$store.commit('jobsModule/SET_ACTIVE_JOB_EDIT_STEP', 3);
              } else {
                this.$store.commit('jobsModule/SET_ACTIVE_JOB_EDIT_STEP', 4);
              }
            }

            if (!this.customer) {
              this.$store.commit('jobsModule/SET_SELECTED_PRESCRIPTION', response.data.data);
            }
          })
          .catch(({ response }) => {
            const errorMessage = response?.data?.error || 'An error occurred while saving the prescription';
            this.$store.dispatch('showErrorAlert', errorMessage);
          })
          .finally(() => {
            if (this.customer) {
              this.$root.$emit('prescriptionListMutated', this.prescription.id);
            }
            this.isLoading = false;
          });
      } else {
        /* eslint-disable */
        if (this.customer) {
          apiPrescriptions
            .postCustomerPrescription(this.customer.id, formData)
            .then(response => {
              const error = this._.get(response, 'data.error');
              const data = this._.get(response, 'data.data');
              if (error) {
                let errorText = error;
                if (data && data.length > 0) {
                  data.map(err => {
                    errorText += ` ${err}`;
                  });
                }
                this.$store.dispatch('showErrorAlert', errorText);
                this.isLoading = false;
                return;
              }
              this.$nextTick(() => {
                this.$refs.newPrescriptionModal.hide('ok');
                this.$store.dispatch(
                  'showSuccessAlert',
                  'New Prescription has been successfully created!',
                );
                this.$root.$emit('prescriptionListMutated', data.id);
              });
            })
            .catch(({ response }) => {
              const errorMessage = response?.data?.error || 'An error occurred while creating the prescription';
              this.$store.dispatch('showErrorAlert', errorMessage);
            })
            .finally(() => {
              this.isLoading = false;
            });
        } else {
          apiPrescriptions
            .postGuestPrescription(formData)
            .then(response => {
              const error = this._.get(response, 'data.error');
              const data = this._.get(response, 'data.data');
              if (error) {
                let errorText = error;
                if (data && data.length > 0) {
                  data.map(err => {
                    errorText += ` ${err}`;
                  });
                }
                this.$store.dispatch('showErrorAlert', errorText);
                this.isLoading = false;
                return;
              }
              this.$nextTick(() => {
                this.$refs.newPrescriptionModal.hide('ok');
                this.$store.dispatch(
                  'showSuccessAlert',
                  'New Prescription has been successfully created!',
                );
                this.$store.commit('jobsModule/SET_SELECTED_PRESCRIPTION', response.data);
                this.$bvModal.show('new-pd-modal');
              });
            })
            .catch(({ response }) => {
              const errorMessage = response?.data?.error || 'An error occurred while creating the prescription';
              this.$store.dispatch('showErrorAlert', errorMessage);
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      }
    },
    handleFileInputChange: async function handleFileInputChange(e) {
      this.fileError = '';
      this.file = null;
      const { valid } = await this.$refs.fileValidator.validate(e);

      if (valid) {
        [this.file] = e.target.files;

        this.isLoading = true;
        try {
          const uploadedFile = await apiFiles.createFile(this.file);
          if (uploadedFile && uploadedFile.data && uploadedFile.data.data) {
            this.newUploadImage = true;
            
            if (this.isEdit && this.prescription && this.prescription.id && this.customer && this.customer.id) {
              await apiFiles.attachFile(
                [uploadedFile.data.data.id],
                this.prescription.id,
                this.customer.id,
              );
              // For edit mode, preserve existing files and add the new one
              const existingFiles = this.form.files || [];
              const updatedFiles = [...existingFiles, uploadedFile.data.data];
              this.$set(this.form, 'files', updatedFiles);
              this.$set(this.prescription, 'files', updatedFiles);
            } else {
              // For new prescriptions, just set the new file
              this.$set(this.form, 'files', [uploadedFile.data.data]);
            }
          } else {
            throw new Error('File upload failed - invalid response');
          }
        } catch (error) {
          console.error('File upload error:', error);
          const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'File upload failed. Please try again.';
          this.fileError = errorMessage;
          this.file = null;
          this.newUploadImage = false;
          this.$store.dispatch('showErrorAlert', errorMessage);
        } finally {
          this.isLoading = false;
        }
      } else {
        this.fileError = 'File must be an image, pdf or doc/docx';
        this.isLoading = false;
      }
    },
    removePrescriptionFile(fileId) {
      this.isLoading = true;
      const request = this.customer
        ? apiPrescriptions.deletePrescriptionFile(this.customer.id, this.prescription.id, fileId)
        : apiPrescriptions.deleteGuestPrescriptionFile(this.prescription.id, fileId);
      request
        .then(() => {
          this.$delete(this.form, 'files');
          this.$delete(this.prescription, 'files');
          this.$store.dispatch('showSuccessAlert', 'File deleted from prescription');
        })
        .catch(() => {
          this.$store.dispatch('showErrorAlert', 'File could not delete');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  watch: {
    isMonocular() {
      // Update add_type based on isMonocular toggle
      this.form.add_type = this.isMonocular ? 'monocular' : 'binocular';
      
      if (this.isMonocular === false && !this.isEdit) {
        this.updateBothNearAdd();
        this.updateBothIntAdd();
      } else if (this.isMonocular === true) {
        // Clear binocular fields when switching to monocular
        this.form.spectacles.add_near = null;
        this.form.spectacles.add_int = null;
      }
    },
  },
  directives: {
    disableIncrement: {
      bind(el) {
        // Disable mouse wheel increment/decrement
        el.addEventListener('wheel', e => {
          e.preventDefault();
        });

        // Disable arrow key increment/decrement
        el.addEventListener('keydown', e => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
          }
        });
      },
    },
  },
};
</script>

<style>
.modal-fullscreen .modal-dialog {
  max-width: 100%;
  margin: 0;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100vh;
  display: flex;
}
#modal-fullscreen-bg {
  background: #fff;
}
</style>
